import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import { Lock, Pin, PinOff, SlidersHorizontal, X } from 'lucide-react';
import * as React from 'react';

import { QuickFilters, type ICompactChip } from '@/design';
import {
  emptyRoot,
  rootChips,
  withoutNodes,
  type ICriteriaGroup,
  type ICriteriaWords,
  type IFilterChip
} from '../../list-criteria';
import { FieldList, type IFieldSection } from './field-picker';

/**
 * The filter in words (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.2 and §3.4): a chip per root condition — "State is NY or CT", "Created since the cycle
 * opened" — each with its ✕; a nested group as one chip, "3 conditions, any of them". Every chip is
 * one of the tree's doors (`rootChips` in `list-criteria.ts` says them all); none spells a code or
 * an operator name (§0.1). A chip's press is the caller's to answer — Lists opens the column's
 * funnel or *Advanced…* on the node. The chips are drawn on a work page's chip line
 * (`listFilterChips`, `workFilterChips`), which gathers those past its end behind "+N".
 *
 * Changing the filter is the screen's state, never saved here: the caller marks the view changed
 * and reads the rows again.
 */

/**
 * One chip, its words cut with an ellipsis past `32rem` (or the row, on a narrow screen): a
 * migrated view's "Firm Name is not …" names dozens of firms, and drawn whole one chip was wider
 * than the screen. The words stay whole where they are read — the press's accessible name, the ✕'s,
 * the problems' list — and a clipped chip says them in a tooltip on hover or focus, a chip that fits
 * none; it is the kit's tooltip, never a `title`, which a keyboard never opens. A work page draws
 * it on its applied filters' row (`workFilterChips`).
 */
export function Chip({
  chip,
  hidden = false,
  invalid,
  onPress,
  onRemove,
  onPin
}: {
  chip: IFilterChip;
  /** Past a condensed line's end, said behind "+N" instead. */
  hidden?: boolean;
  invalid: boolean;
  onPress: (from: HTMLElement) => void;
  onRemove: () => void;
  /** *Pin for everyone who opens this view* (SR-412), where the reader may. */
  onPin?: () => void;
}) {
  const press = React.useRef<HTMLButtonElement>(null);
  const [whole, setWhole] = React.useState(false);
  // Measured as the tooltip asks to open, not observed: the row reflows with every chip and column.
  // A pixel's slack, the kit's `useOverflow`'s: a fitting chip's widths can round apart.
  function clipped(): boolean {
    const element = press.current;
    return element !== null && element.scrollWidth > element.clientWidth + 1;
  }
  return (
    <span
      hidden={hidden}
      data-chip={chip.field ?? ''}
      data-chip-key={chip.key}
      data-invalid={invalid ? '' : undefined}
      className={
        'inline-flex min-w-0 max-w-[32rem] items-center rounded-full border bg-[var(--card)] text-[0.8125rem] ' +
        (invalid ? 'border-destructive' : 'border-border')
      }
    >
      <Tooltip open={whole} onOpenChange={(open) => setWhole(open && clipped())}>
        <TooltipTrigger asChild>
          {/* No description: the tooltip repeats the name, and a reader would hear the words twice. */}
          <button
            ref={press}
            type="button"
            aria-describedby={undefined}
            onClick={(event) => onPress(event.currentTarget)}
            className="min-w-0 truncate rounded-l-full py-0.5 pl-2.5 pr-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {chip.words}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-h-[60dvh] max-w-lg overflow-y-auto break-words">{chip.words}</TooltipContent>
      </Tooltip>
      {onPin && (
        <ChipIconButton label={`Pin for everyone who opens this view: ${chip.words}`} onPress={onPin}>
          <Pin className="size-3.5" aria-hidden />
        </ChipIconButton>
      )}
      <button
        type="button"
        aria-label={`Remove the filter: ${chip.words}`}
        onClick={onRemove}
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </span>
  );
}

/** A chip's small icon press, named by `label`, with the kit's tooltip. */
function ChipIconButton({ label, onPress, children }: { label: string; onPress: () => void; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onPress}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label.split(':')[0]}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A view's pinned condition (SR-412): a lock, the words, and for who may save over the view
 * *Unpin* (back into the filter) and ✕; for everyone else nothing to press — the tooltip says
 * whose it is. *Clear all* and the funnels never reach it.
 */
function PinnedChip({
  chip,
  invalid,
  onUnpin
}: {
  chip: IFilterChip;
  /** survey-api refused it — the screen says why under the chips. */
  invalid: boolean;
  onUnpin?: (nodeIds: readonly string[], keep: boolean) => void;
}) {
  return (
    <span
      data-chip-pinned={chip.field ?? ''}
      data-invalid={invalid ? '' : undefined}
      className={cn(
        'inline-flex min-w-0 max-w-[32rem] items-center gap-1 rounded-full border bg-[var(--color-slight-blue)] py-0.5 pl-2 text-[0.8125rem] text-[var(--color-royal-blue)]',
        invalid ? 'border-destructive' : 'border-[var(--veritant-royal-30,var(--border))]'
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            aria-label={`Pinned by the view's owner: ${chip.words}`}
            className="inline-flex min-w-0 items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Lock className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{chip.words}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Pinned by the view’s owner — on for everyone who opens this view. Add your own filters
          beside it.
        </TooltipContent>
      </Tooltip>
      {onUnpin ? (
        <>
          <ChipIconButton label={`Unpin: ${chip.words}`} onPress={() => onUnpin(chip.nodeIds, true)}>
            <PinOff className="size-3.5" aria-hidden />
          </ChipIconButton>
          <ChipIconButton label={`Remove the pinned filter: ${chip.words}`} onPress={() => onUnpin(chip.nodeIds, false)}>
            <X className="size-3.5" aria-hidden />
          </ChipIconButton>
        </>
      ) : (
        <span className="pr-2" />
      )}
    </span>
  );
}

/**
 * Lists' filters applied (SR-412), as the work pages' `CompactChipLine` draws them: the view's pinned
 * conditions first, locked, then the filter's own chips — each pressed to its funnel or *Advanced…*,
 * ✕ to remove, 📌 where the reader may pin. The name search makes no chip: its box shows it.
 */
export function listFilterChips({
  root,
  words,
  pinned,
  problemsOf,
  onChange,
  onChipPress,
  canPin,
  onPin,
  onUnpin
}: {
  root: ICriteriaGroup;
  words: ICriteriaWords;
  pinned: ICriteriaGroup;
  problemsOf: (nodeId: string) => readonly string[];
  onChange: (root: ICriteriaGroup) => void;
  onChipPress: (chip: IFilterChip, from: HTMLElement) => void;
  canPin: boolean;
  onPin: (nodeIds: readonly string[]) => void;
  onUnpin?: (nodeIds: readonly string[], keep: boolean) => void;
}): ICompactChip[] {
  return [
    ...rootChips(pinned, words).map((chip) => ({
      key: `pinned:${chip.key}`,
      chip: (
        <PinnedChip
          chip={chip}
          invalid={chip.nodeIds.some((id) => problemsOf(id).length > 0)}
          onUnpin={onUnpin}
        />
      )
    })),
    ...rootChips(root, words)
      .filter((chip) => chip.door !== 'name')
      .map((chip) => ({
        key: chip.key,
        chip: (
          <Chip
            chip={chip}
            invalid={chip.nodeIds.some((id) => problemsOf(id).length > 0)}
            onPress={(from) => onChipPress(chip, from)}
            onRemove={() => onChange(withoutNodes(root, chip.nodeIds))}
            onPin={canPin ? () => onPin(chip.nodeIds) : undefined}
          />
        )
      }))
  ];
}

/**
 * *Filters (n)* (SR-412) — the filter's one door in the toolbar, where Conga's *Filters* was. It opens
 * a search over every field the list can filter on, so a field is found by typing: one chosen goes
 * to `onField` (its column's funnel, or *Advanced…* with a condition on it). Below the fields,
 * *Advanced…* (every condition, all / any) and, while there is one, *Clear all (n)*. The count is
 * the chips'.
 *
 * On a work page (SR-530) the same door holds the page's quick filters above the field search when
 * the toolbar has folded them, and always in the compact bar — one menu, never a *Filters* beside a
 * *Filters*; `count` and `onClearAll` are then the page's, its quick filters and signals included,
 * and its open state the toolbar's, so a chip of the applied filters opens a quick filter inside it.
 */
export function FiltersButton({
  root,
  words,
  sections,
  onField,
  onAdvanced,
  onChange,
  quick,
  count: counted,
  onClearAll,
  open: held,
  onOpenChange
}: {
  root: ICriteriaGroup;
  words: ICriteriaWords;
  sections: readonly IFieldSection[];
  /** A field chosen; `from` takes the focus back as what it opened closes. */
  onField: (code: string, from: HTMLElement) => void;
  onAdvanced: () => void;
  onChange: (root: ICriteriaGroup) => void;
  /** The page's quick filters, drawn first; none where the toolbar draws them itself. */
  quick?: React.ReactNode;
  /** The filters applied, where the page counts more than the tree's chips. */
  count?: number;
  /** *Clear all*, where the page clears more than the tree. */
  onClearAll?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [own, setOwn] = React.useState(false);
  const open = held ?? own;
  const setOpen = onOpenChange ?? setOwn;
  const trigger = React.useRef<HTMLButtonElement>(null);
  const count = counted ?? rootChips(root, words).length;
  const quicked = quick !== undefined && quick !== null && quick !== false;
  const close = (then: () => void) => {
    setOpen(false);
    then();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={trigger}
          type="button"
          variant="outline"
          className="h-8 shrink-0 gap-1.5 px-2.5 font-normal"
          aria-label={count > 0 ? `Filters, ${count} set` : 'Filters'}
          data-filters-menu=""
        >
          <SlidersHorizontal className="size-3.5" aria-hidden />
          Filters{count > 0 && ` (${count})`}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={quicked ? 'start' : 'end'}
        aria-label="Filters"
        className="max-h-[var(--radix-popover-content-available-height)] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto p-0 text-[0.8125rem]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          // A quick filter opened from its chip takes the focus itself; the field search otherwise.
          if (quicked) return;
          (event.currentTarget as HTMLElement).querySelector<HTMLElement>('input[type="search"]')?.focus();
        }}
      >
        {quicked && (
          <div data-filters-quick="" className="grid gap-2 border-b border-border px-3 pt-2.5 pb-3">
            <p className="text-xs font-medium text-[var(--veritant-royal-70)]">Quick filters</p>
            <div className="flex flex-wrap items-center gap-2">
              <QuickFilters>{quick}</QuickFilters>
            </div>
          </div>
        )}
        {sections.length > 0 && (
          <>
            <p className="px-3 pt-2.5 text-xs font-medium text-[var(--veritant-royal-70)]">Filter by a field</p>
            <FieldList
              sections={sections}
              value=""
              codes={false}
              placeholder="Type a field’s name or number…"
              onChoose={(code) => close(() => onField(code, trigger.current ?? document.body))}
            />
          </>
        )}
        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-border bg-popover px-3 py-2">
          <Button type="button" variant="link" className="h-auto p-0 text-[0.8125rem]" onClick={() => close(onAdvanced)}>
            Advanced…
          </Button>
          {count > 0 && (
            <Button
              type="button"
              variant="ghost"
              className="h-7 gap-1 px-2 font-normal"
              onClick={() => close(() => (onClearAll ? onClearAll() : onChange(emptyRoot())))}
            >
              Clear all ({count})
              <X className="size-3.5" aria-hidden />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
